import { createContext, useContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

interface DisplayPreferences {
  font: "geist" | "lexend" | "system";
  letterSpacing: 1 | 1.25 | 1.5;
  lineHeight: 1.5 | 1.75 | 2;
  bgTint: "none" | "warm" | "cool";
  highContrast: boolean;
  textScale: 1 | 1.125 | 1.25;
}

interface DisplayPreferencesCtx {
  preferences: DisplayPreferences;
  setPreference: <K extends keyof DisplayPreferences>(key: K, value: DisplayPreferences[K]) => void;
  resetAll: () => void;
  isCustomized: boolean;
}

const STORAGE_KEY = "alchema_display"; // Neutral key — no health references (GDPR Art.9)

const DEFAULTS: DisplayPreferences = {
  font: "geist",
  letterSpacing: 1,
  lineHeight: 1.5,
  bgTint: "none",
  highContrast: false,
  textScale: 1,
};

function getStored(): DisplayPreferences {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = { ...DEFAULTS, ...JSON.parse(raw) };
    // Validate and clamp to allowed values (defense against corrupt localStorage)
    if (![1, 1.25, 1.5].includes(parsed.letterSpacing)) parsed.letterSpacing = DEFAULTS.letterSpacing;
    if (![1.5, 1.75, 2].includes(parsed.lineHeight)) parsed.lineHeight = DEFAULTS.lineHeight;
    if (!["geist", "lexend", "system"].includes(parsed.font)) parsed.font = DEFAULTS.font;
    if (!["none", "warm", "cool"].includes(parsed.bgTint)) parsed.bgTint = DEFAULTS.bgTint;
    if (![1, 1.125, 1.25].includes(parsed.textScale)) parsed.textScale = DEFAULTS.textScale;
    if (typeof parsed.highContrast !== "boolean") parsed.highContrast = DEFAULTS.highContrast;
    return parsed;
  } catch {
    return DEFAULTS;
  }
}

const Ctx = createContext<DisplayPreferencesCtx>({
  preferences: DEFAULTS,
  setPreference: () => {},
  resetAll: () => {},
  isCustomized: false,
});

export function DisplayPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<DisplayPreferences>(getStored);

  const isCustomized = useMemo(
    () => JSON.stringify(preferences) !== JSON.stringify(DEFAULTS),
    [preferences],
  );

  // Apply preferences to document
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--user-letter-spacing", `${preferences.letterSpacing}px`);
    root.style.setProperty("--user-line-height", `${preferences.lineHeight}`);

    // Font selection — Lexend loaded on demand for ND users
    if (preferences.font === "lexend") {
      import("@fontsource-variable/lexend/index.css").catch(() => {});
      root.style.setProperty("--font-body", "'Lexend Variable', 'Geist', system-ui, sans-serif");
    } else if (preferences.font === "system") {
      root.style.setProperty("--font-body", "system-ui, -apple-system, sans-serif");
    } else {
      root.style.removeProperty("--font-body");
    }

    // Background tint
    if (preferences.bgTint === "warm") {
      root.style.setProperty("--bg-tint", "2deg");
    } else if (preferences.bgTint === "cool") {
      root.style.setProperty("--bg-tint", "-2deg");
    } else {
      root.style.removeProperty("--bg-tint");
    }

    // High contrast mode — toggle CSS class for enhanced contrast tokens
    if (preferences.highContrast) {
      root.classList.add("high-contrast");
    } else {
      root.classList.remove("high-contrast");
    }

    // Text scale — apply font-size multiplier to root
    if (preferences.textScale !== 1) {
      root.style.setProperty("--user-text-scale", `${preferences.textScale}`);
      root.style.fontSize = `${preferences.textScale * 100}%`;
    } else {
      root.style.removeProperty("--user-text-scale");
      root.style.removeProperty("font-size");
    }
  }, [preferences]);

  // Persist to localStorage
  useEffect(() => {
    if (isCustomized) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [preferences, isCustomized]);

  const setPreference = useCallback(<K extends keyof DisplayPreferences>(
    key: K,
    value: DisplayPreferences[K],
  ) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetAll = useCallback(() => {
    setPreferences(DEFAULTS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ preferences, setPreference, resetAll, isCustomized }),
    [preferences, setPreference, resetAll, isCustomized],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDisplayPreferences() {
  return useContext(Ctx);
}
